import { isError } from 'util';

import { Box, Button, Typography, Grid, Stack, Divider } from '@mui/material';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  useNavigate,
  Link,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { LoadingSpinner } from '@app/components/Shared/LoadingSpinner';
import { getProduct } from '@app/domain/network-actions';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { useGADataLayer } from '@app/hooks/useGADataLayer';
import { useSetSnackbar } from '@app/hooks/useSetSnackbar';
import { useUTMParams } from '@app/hooks/useUTMTracker';
import { GA_CUSTOM_EVENT } from '@app/static/constants';
import { theme } from '@app/theme';
import { Product } from '@app/types/catalog';

interface CompletedProps {
  isExhibition: boolean;
}

export function Completed({ isExhibition }: CompletedProps): ReactElement {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { isMobile } = useDeviceType();
  const [isLoading, setIsLoading] = useState(false);
  const setSnackbar = useSetSnackbar();
  const [product, setProduct] = useState<Product | null>(null);
  const utmParams = useUTMParams();
  const { sendGAConversionCompleteEvent } = useGADataLayer();

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    try {
      setIsLoading(true);
      const result = await getProduct(productId);
      setProduct(result.data);
    } catch (e) {
      if (isError(e)) {
        console.error('fetchProduct', e.message);
      }
      setSnackbar(true, '住宅展示場が見つかりません');
    } finally {
      setIsLoading(false);
    }
  }, [productId, setSnackbar]);

  useEffect(() => {
    if (orderId) {
      sendGAConversionCompleteEvent(
        GA_CUSTOM_EVENT.INQUIRY_SUBMIT_COMPLETED,
        orderId
      );
      utmParams.clear();
    }
    void fetchProduct();
    // eslint-disable-next-line
  }, []);

  const organization =
    typeof product?.organization !== 'string' ? product?.organization : null;

  const handleGoToTopPage = () => {
    navigate('/');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  return (
    <Stack
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      spacing={{ md: 4, sm: 4, xs: 3 }}
      padding={{ md: '40px 50px', sm: 4, xs: 4 }}
      marginX="auto"
      width="100%"
      sx={{ backgroundColor: '#f5f8fc' }}
    >
      <Grid
        borderRadius={2}
        container
        direction="column"
        alignItems="center"
        padding={isMobile ? '25px 25px' : '45px 50px'}
        textAlign="center"
        width="90vw"
        maxWidth={1000}
        sx={{ backgroundColor: '#ffffff' }}
      >
        <Typography
          variant="h1"
          fontWeight="bold"
          color={theme.palette.blueAccent.main}
        >
          {isExhibition ? '予約を受け付けました' : '見学の予約が完了しました'}
        </Typography>
        <Divider
          color={theme.palette.blueAccent.main}
          sx={{
            maxWidth: 900,
            mb: 2,
            mt: 6,
            mx: 'auto',
            width: '100%',
          }}
          orientation="horizontal"
          flexItem
        />
        <Stack
          pt={2}
          spacing={1.5}
          textAlign={{ md: 'center', sm: 'left', xs: 'left' }}
        >
          <Typography>
            {isExhibition
              ? '展展示場へのご来場予約をいただき、誠にありがとうございます。'
              : '物件の見学のご予約をいただき、誠にありがとうございます。'}
          </Typography>
          <Typography>
            ご入力いただいたメールアドレス宛に、後日予約確認メールを送信いたします。
          </Typography>
          <Typography>
            数日経ってもメールが届かない場合は、大変お手数ではございますが、下記へお問い合わせください。
          </Typography>
          <Typography color="#00A0FF" pt={2} textAlign={'center'}>
            <Link
              to="https://pro-cloud.jp/contact"
              target="_blank"
              style={{ color: '#00A0FF' }}
            >
              メールでのお問い合わせはこちら
            </Link>
          </Typography>
        </Stack>
      </Grid>

      <Grid
        borderRadius={2}
        container
        direction="column"
        alignItems="center"
        maxWidth={1000}
        mt={5}
        padding={isMobile ? '25px 25px' : '45px 50px'}
        textAlign="left"
        width="90vw"
        sx={{ backgroundColor: '#ffffff' }}
      >
        {isLoading ? (
          <LoadingSpinner sx={{ mb: 4 }} />
        ) : (
          <Stack
            border={`1px solid ${theme.palette.neutral.silver}`}
            borderRadius={2}
            color={theme.palette.text.primary}
            padding={{ md: 5, xs: 3 }}
            spacing={1.5}
            width="100%"
          >
            <Typography fontWeight={'bold'} mb={3}>
              お問い合わせ先
            </Typography>
            <Typography>{organization?.name || '-'}</Typography>
            {product?.customFields.contactAddress && (
              <Typography>
                問い合わせ先名：{product.customFields.contactAddress}
              </Typography>
            )}
            {product?.customFields.contactPhoneNumber && (
              <Typography>
                問い合わせ先電話番号：{product.customFields.contactPhoneNumber}
              </Typography>
            )}
            {product?.customFields.contactAdditional && (
              <Typography>
                問い合わせ先補足：{product.customFields.contactAdditional}
              </Typography>
            )}
            <Typography>
              ※休業日にいただいたお問い合わせにつきましては、翌営業日でのご対応とさせていただきます。
            </Typography>
          </Stack>
        )}

        <Box
          bgcolor="#DEF0FA"
          borderRadius={2}
          mt={5}
          mx="auto"
          padding={{ md: 5, xs: 3 }}
          textAlign="center"
          width="100%"
        >
          <Typography variant="h6" fontWeight="bold" mb={2}>
            会員登録
          </Typography>
          <Typography mb={4}>
            会員登録をすると、次の問い合わせの際にお客様情報の入力を省略できるようになります
          </Typography>
          <Button
            onClick={handleRegister}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.blueAccent.main,
              },
              backgroundColor: theme.palette.blueAccent.main,
              borderRadius: 3,
              color: '#ffffff',
              fontSize: 24,
              height: { md: 60, xs: 48 },
              padding: 5,
              width: '100%',
            }}
          >
            会員登録をする
          </Button>
        </Box>
        <Button
          onClick={handleGoToTopPage}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.blueAccent.main,
              color: '#ffffff',
            },
            backgroundColor: '#ffffff',
            border: `solid 1px ${theme.palette.blueAccent.main}`,
            borderRadius: 10,
            color: theme.palette.blueAccent.main,
            maxWidth: '100vw',
            mt: 4,
            mx: 'auto',
            textTransform: 'none',
            width: { md: 250, sm: 250, xs: '100%' },
          }}
        >
          トップページへ
        </Button>
        <Box mt={3} textAlign={'center'}>
          <Link
            to={
              isExhibition
                ? `/exhibitions/${productId}`
                : `/properties/${productId}`
            }
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
            }}
          >
            {isExhibition ? '展示場詳細ページ' : '物件詳細ページ'}
          </Link>
        </Box>
      </Grid>
    </Stack>
  );
}
